import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { ChevronLeft, ChevronRight, Save, Send, Trash2, AlertTriangle, MessageSquare, MessageSquarePlus, Briefcase, Clock, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const decimalToHHMM = (decimal) => {
  if (!decimal || decimal <= 0) return '';
  const totalMinutes = Math.round(decimal * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const hhmmToDecimal = (str) => {
  if (!str || str.trim() === '') return 0;
  if (str.includes(':')) {
    const [hPart, mPart] = str.split(':');
    const h = parseInt(hPart, 10) || 0;
    const m = Math.min(59, parseInt(mPart, 10) || 0);
    return h + m / 60;
  }
  return parseFloat(str) || 0;
};

const TimeEntryPage = () => {
  const { api, user, isAdmin, isSuperAdmin, isManager } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showDemoRow, setShowDemoRow] = useState(false);
  const [entries, setEntries] = useState([]);
  const [editWindowDays, setEditWindowDays] = useState(15);
  const [timesheet, setTimesheet] = useState(null);
  const [approvedTimesheet, setApprovedTimesheet] = useState(null);
  const [approvedTimesheetIds, setApprovedTimesheetIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const getInitialWeekOffset = () => {
    const weekParam = searchParams.get('week');
    if (!weekParam) return 0;
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() + mondayOffset);
    currentMonday.setHours(0, 0, 0, 0);
    const targetMonday = new Date(weekParam + 'T00:00:00');
    return Math.round((targetMonday - currentMonday) / (7 * 24 * 60 * 60 * 1000));
  };

  const [weekOffset, setWeekOffset] = useState(getInitialWeekOffset);
  const [projectHoursUsed, setProjectHoursUsed] = useState({});
  const [monthlyProjectHoursUsed, setMonthlyProjectHoursUsed] = useState({});
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [catalogVerticals, setCatalogVerticals] = useState([]);
  const [catalogWorkTypes, setCatalogWorkTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('you');
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamData, setTeamData] = useState({});
  const [teamProjectsMap, setTeamProjectsMap] = useState({});
  const [teamLoading, setTeamLoading] = useState(false);

  // Calculate week dates
  const { weekStart, weekEnd, weekDates } = useMemo(() => {
    const toLocalDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset + (weekOffset * 7));
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(toLocalDate(date));
    }

    return {
      weekStart: toLocalDate(monday),
      weekEnd: toLocalDate(sunday),
      weekDates: dates,
    };
  }, [weekOffset]);

  useEffect(() => {
    fetchCatalog();
    api.get('/settings').then(r => setEditWindowDays(r.data.time_entry_edit_days ?? 15)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isManager) return;
    api.get('/team/hierarchy').then(res => {
      const reports = (res.data.all_users || []).filter(u => u.manager_id === user.id);
      setTeamMembers(reports);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  useEffect(() => {
    const onStart = (e) => { if (e.detail?.pathname === '/time-entry') setShowDemoRow(true); };
    const onEnd = () => setShowDemoRow(false);
    window.addEventListener('workintel:tour:start', onStart);
    window.addEventListener('workintel:tour:end', onEnd);
    return () => {
      window.removeEventListener('workintel:tour:start', onStart);
      window.removeEventListener('workintel:tour:end', onEnd);
    };
  }, []);

  useEffect(() => {
    if (selectedProjects.length > 0) setShowDemoRow(false);
  }, [selectedProjects.length]);


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, weekEnd]);

  useEffect(() => {
    if (activeTab === 'team' && teamMembers.length > 0) fetchTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, weekStart, weekEnd, teamMembers.length]);

  const fetchCatalog = async () => {
    try {
      const [vRes, wRes] = await Promise.all([
        api.get('/service-catalog/verticals'),
        api.get('/service-catalog/work-types'),
      ]);
      setCatalogVerticals(vRes.data);
      setCatalogWorkTypes(wRes.data);
    } catch (err) {
      // catalog is optional — don't block page
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;

      const [projectsRes, entriesRes, timesheetRes, allUserEntriesRes, monthEntriesRes] = await Promise.all([
        api.get('/projects?status=active'),
        api.get(`/time-entries?date_from=${weekStart}&date_to=${weekEnd}&user_id=${user.id}`),
        api.get(`/timesheets?user_id=${user.id}`),
        api.get(`/time-entries?user_id=${user.id}`),
        api.get(`/time-entries?date_from=${monthStart}&date_to=${monthEnd}&user_id=${user.id}`),
      ]);

      // Separate approved (read-only) from active editable timesheet for the week.
      const weekTimesheets = timesheetRes.data.filter(t => t.week_start === weekStart);
      const approvedTs = weekTimesheets.find(t => t.status === 'approved') || null;
      const activeTs = weekTimesheets.find(t => ['draft', 'submitted', 'rejected'].includes(t.status)) || null;
      const approvedIds = new Set(weekTimesheets.filter(t => t.status === 'approved').map(t => t.id));
      setApprovedTimesheet(approvedTs);
      setApprovedTimesheetIds(approvedIds);
      setTimesheet(activeTs);

      let availableProjects = [...projectsRes.data].sort((a, b) => a.name.localeCompare(b.name));
      if (!isAdmin && !isSuperAdmin) {
        availableProjects = availableProjects.filter(p =>
          p.allocations?.some(a => a.user_id === user?.id)
        );
      }
      setEntries(entriesRes.data);

      // All-time hours per project
      const hoursUsed = {};
      for (const entry of allUserEntriesRes.data) {
        hoursUsed[entry.project_id] = (hoursUsed[entry.project_id] || 0) + (entry.hours || 0);
      }
      setProjectHoursUsed(hoursUsed);

      // Current-month hours per project (used for retainer/services projects)
      const monthlyHoursUsed = {};
      for (const entry of monthEntriesRes.data) {
        monthlyHoursUsed[entry.project_id] = (monthlyHoursUsed[entry.project_id] || 0) + (entry.hours || 0);
      }
      setMonthlyProjectHoursUsed(monthlyHoursUsed);

      // All available projects shown by default
      setSelectedProjects(availableProjects);

    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamData = async () => {
    setTeamLoading(true);
    try {
      const allProjectsRes = await api.get('/projects?status=active');
      const pMap = {};
      allProjectsRes.data.forEach(p => { pMap[p.id] = p; });
      setTeamProjectsMap(pMap);

      const results = await Promise.all(
        teamMembers.map(async (member) => {
          const [entriesRes, tsRes] = await Promise.all([
            api.get(`/time-entries?date_from=${weekStart}&date_to=${weekEnd}&user_id=${member.id}`),
            api.get(`/timesheets?user_id=${member.id}`),
          ]);
          const weekTs = tsRes.data.filter(t => t.week_start === weekStart);
          const ts = weekTs.find(t => t.status === 'approved')
            || weekTs.find(t => ['submitted', 'rejected', 'draft'].includes(t.status))
            || null;
          return { memberId: member.id, entries: entriesRes.data, timesheet: ts };
        })
      );
      const map = {};
      for (const r of results) map[r.memberId] = { entries: r.entries, timesheet: r.timesheet };
      setTeamData(map);
    } catch {
      toast.error('Failed to fetch team data');
    } finally {
      setTeamLoading(false);
    }
  };

  const getEntryForCell = (projectId, date) => {
    return entries.find(e => e.project_id === projectId && e.date === date);
  };

  const handleCellChange = async (projectId, date, hours, entry) => {
    const hoursNum = parseFloat(hours) || 0;

    try {
      if (entry) {
        if (hoursNum === 0) {
          // Delete entry
          await api.delete(`/time-entries/${entry.id}`);
          setEntries(prev => prev.filter(e => e.id !== entry.id));
          setProjectHoursUsed(prev => ({
            ...prev,
            [projectId]: (prev[projectId] || 0) - entry.hours
          }));
        } else {
          // Skip API call if hours haven't changed
          if (hoursNum === entry.hours) return;
          // Update entry
          const hoursDiff = hoursNum - entry.hours;
          const { data: updated } = await api.put(`/time-entries/${entry.id}`, { hours: hoursNum });
          setEntries(prev => prev.map(e => e.id === entry.id ? updated : e));
          setProjectHoursUsed(prev => ({
            ...prev,
            [projectId]: (prev[projectId] || 0) + hoursDiff
          }));
        }
      } else if (hoursNum > 0) {
        // Create entry
        const res = await api.post('/time-entries', {
          project_id: projectId,
          date: date,
          hours: hoursNum,
          is_billable: true,
          description: '',
        });
        setEntries(prev => [...prev, res.data]);
        setProjectHoursUsed(prev => ({
          ...prev,
          [projectId]: (prev[projectId] || 0) + hoursNum
        }));
      }
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save entry'));
    }
  };

const removeProjectRow = async (projectId) => {
    const projectEntries = entries.filter(e => e.project_id === projectId);
    if (projectEntries.length > 0) {
      if (isAdmin || isSuperAdmin) {
        try {
          for (const entry of projectEntries) {
            await api.delete(`/time-entries/${entry.id}`);
          }
          setEntries(prev => prev.filter(e => e.project_id !== projectId));
          setSelectedProjects(prev => prev.filter(p => p.id !== projectId));
          setProjectHoursUsed(prev => { const n = { ...prev }; delete n[projectId]; return n; });
          toast.success('Time entries deleted');
        } catch (err) {
          toast.error(getApiError(err, 'Failed to delete time entries'));
        }
      } else {
        toast.error('Cannot remove project with time entries. Delete the entries first.');
      }
      return;
    }
    setSelectedProjects(prev => prev.filter(p => p.id !== projectId));
  };

  const handleDescriptionSave = async (entry, description, vertical_id, work_type_id, blockers) => {
    if (!entry) return;
    try {
      const payload = { description, vertical_id: vertical_id || null, work_type_id: work_type_id || null, blockers: blockers || null };
      await api.put(`/time-entries/${entry.id}`, payload);
      setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, description, vertical_id, work_type_id, blockers } : e));
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save description'));
    }
  };

  const createTimesheet = async () => {
    try {
      const res = await api.post('/timesheets', {
        week_start: weekStart,
        week_end: weekEnd,
      });
      setTimesheet(res.data);
      toast.success('Timesheet created');
      return res.data;
    } catch (err) {
      toast.error(getApiError(err, 'Failed to create timesheet'));
      return null;
    }
  };

  const submitTimesheet = async () => {
    // Use the active (non-approved) timesheet; create one if needed.
    let ts = timesheet;
    if (!ts || ts.status === 'approved') {
      ts = await createTimesheet();
      if (!ts) return;
    }

    // If already submitted and no new unlinked entries exist, block early.
    if (ts.status === 'submitted') {
      const hasNewEntries = entries.some(e => !e.timesheet_id);
      if (!hasNewEntries) {
        toast.error('Timesheet already submitted');
        return;
      }
    }

    try {
      await api.post(`/timesheets/${ts.id}/submit`);
      toast.success('Timesheet submitted for approval');
      fetchData();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to submit timesheet'));
    }
  };

  const calculateDayTotal = (date) => {
    return entries.filter(e => e.date === date).reduce((sum, e) => sum + e.hours, 0);
  };

  const calculateProjectTotal = (projectId) => {
    return entries.filter(e => e.project_id === projectId).reduce((sum, e) => sum + e.hours, 0);
  };

  const calculateWeekTotal = () => {
    return entries.reduce((sum, e) => sum + e.hours, 0);
  };

  const isRetainerProject = (project) =>
    project.project_type === 'monthly_retainer' && (project.monthly_budget_hours || 0) > 0;

  const getHoursUsed = (project) =>
    isRetainerProject(project)
      ? (monthlyProjectHoursUsed[project.id] || 0)
      : (projectHoursUsed[project.id] || 0);

  const getAllocatedHours = (project) => {
    if (isAdmin || isSuperAdmin) {
      return isRetainerProject(project)
        ? (project.monthly_budget_hours || 0)
        : (project.budgeted_hours || 0);
    }
    const allocation = project.allocations?.find(a => a.user_id === user.id);
    return allocation?.allocated_hours || 0;
  };

  const getRemainingHours = (project) => {
    const allocated = getAllocatedHours(project);
    const used = getHoursUsed(project);
    return Math.max(0, allocated - used);
  };

  const getUtilizationPercent = (project) => {
    const allocated = getAllocatedHours(project);
    if (allocated === 0) return 0;
    const used = getHoursUsed(project);
    return Math.min(100, (used / allocated) * 100);
  };

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // Lock the entire week for regular users when it has an approved timesheet.
  // Admins/super-admins bypass the lock so they can still make corrections.
  const isApprovedWeek = !!approvedTimesheet;
  const isLocked = isApprovedWeek && !isAdmin && !isSuperAdmin;
  const canDelete = !isLocked || isAdmin || isSuperAdmin;
  // Entry is read-only if it belongs to ANY approved timesheet for the week. Admins can always edit.
  const isEntryReadOnly = (entry) => {
    if (isAdmin || isSuperAdmin) return false;
    return entry && entry.timesheet_id && approvedTimesheetIds.has(entry.timesheet_id);
  };
  // ID of the active rejected timesheet (if any) — used to mark its entries with ⚠
  const rejectedTimesheetId = timesheet?.status === 'rejected' ? timesheet?.id : null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isDateRestricted = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (d > today) return true;   // future dates blocked for everyone
    if (isAdmin) return false;    // admins can access any past date
    if (editWindowDays === 0) return false; // no limit configured
    const minAllowedDate = new Date(today);
    minAllowedDate.setDate(today.getDate() - editWindowDays);
    return d < minAllowedDate;
  };

  const canNavigateForward = () => {
    // Future weeks blocked for everyone
    const nextMonday = new Date(weekStart);
    nextMonday.setDate(nextMonday.getDate() + 7);
    return nextMonday <= today;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-entry-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
              <Clock className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Weekly Timesheet</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Time Entry</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary/60" />
                {activeTab === 'team' ? "Overview of your team's hours for the week" : 'Log your hours for the week'}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="outline" size="icon" className="flex-shrink-0 bg-background/70 backdrop-blur-sm" onClick={() => setWeekOffset(prev => prev - 1)} data-testid="prev-week-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 px-3 py-2 border border-border rounded-sm text-sm font-medium text-center sm:min-w-[200px] bg-background/70 backdrop-blur-sm">
                {new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(weekEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
              <Button variant="outline" size="icon" className="flex-shrink-0 bg-background/70 backdrop-blur-sm" onClick={() => setWeekOffset(prev => prev + 1)} data-testid="next-week-btn" disabled={!canNavigateForward()}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="outline" onClick={() => setWeekOffset(0)} className="w-full sm:w-auto bg-background/70 backdrop-blur-sm">
              Today
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Switcher — managers only */}
      {isManager && teamMembers.length > 0 && (
        <div className="flex gap-1 border-b border-border -mb-2">
          <button
            data-tour="time-entry-you-tab"
            onClick={() => setActiveTab('you')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'you'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            You
          </button>
          <button
            data-tour="time-entry-team-tab"
            onClick={() => setActiveTab('team')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'team'
                ? 'border-accent text-accent'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Team <span className="ml-1 text-xs bg-muted px-1.5 py-0.5 rounded-full">{teamMembers.length}</span>
          </button>
        </div>
      )}

      {/* Rejection Banner */}
      {activeTab === 'you' && timesheet?.status === 'rejected' && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0">
            <div className="text-sm font-medium text-red-600 dark:text-red-400">Timesheet Rejected</div>
            {timesheet.rejection_reason && (
              <div className="text-sm text-red-500/80 whitespace-pre-line">{timesheet.rejection_reason}</div>
            )}
            <div className="text-xs text-muted-foreground mt-1">Fix the flagged entries (marked ⚠ in the grid) and resubmit.</div>
          </div>
        </div>
      )}

      {/* Add Project Row — hidden; all projects are shown by default */}

      {/* Time Grid — Desktop */}
      {activeTab === 'you' && <Card data-testid="time-grid" className="hidden md:block">
        <CardContent className="p-0">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border/60 bg-muted/30 text-xs text-muted-foreground flex-wrap">
            <Clock className="w-3.5 h-3.5 text-primary/50 flex-shrink-0" />
            <span className="font-semibold text-foreground/70">Enter time as H:MM —</span>
            {[['0:15','15 min'],['0:25','25 min'],['0:30','30 min'],['0:45','45 min'],['1:00','1 hr'],['1:30','1.5 hr']].map(([hhmm, label], i) => (
              <span key={hhmm} className="flex items-center gap-1">
                {i > 0 && <span className="text-muted-foreground/40">·</span>}
                <span className="font-mono bg-muted px-1 py-0.5 rounded text-foreground/80">{hhmm}</span>
                <span>= {label}</span>
              </span>
            ))}
          </div>
          <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-medium text-muted-foreground w-[250px]">Project</th>
                  {weekDates.map((date, i) => {
                    const restricted = isDateRestricted(date);
                    return (
                      <th key={date} className={`text-center p-4 font-medium w-[80px] ${restricted ? 'text-muted-foreground/40' : 'text-muted-foreground'}`}>
                        <div className="text-xs">{dayNames[i]}</div>
                        <div className="text-sm mt-1">{new Date(date).getDate()}</div>
                        {restricted && <div className="text-[10px] mt-0.5 text-muted-foreground/50">{new Date(date + 'T00:00:00') > today ? 'future' : 'locked'}</div>}
                      </th>
                    );
                  })}
                  <th className="text-center p-4 font-medium text-muted-foreground w-[80px]">Total</th>
                  {canDelete && <th className="w-[50px]"></th>}
                </tr>
              </thead>
              <tbody>
                {selectedProjects.map((project, projectIndex) => {
                  const allocated = getAllocatedHours(project);
                  const remaining = getRemainingHours(project);
                  const used = getHoursUsed(project);
                  const isOverAllocated = allocated > 0 && used > allocated;
                  const isNearLimit = !isOverAllocated && allocated > 0 && getUtilizationPercent(project) >= 80;
                  const showAlloc = allocated > 0 && !isAdmin && !isSuperAdmin;

                  const getMaxForCell = (cellEntry) => {
                    if (!allocated || isAdmin || isSuperAdmin) return null;
                    return Math.max(0, remaining + (cellEntry?.hours || 0));
                  };

                  return (
                    <tr key={project.id} className="border-b border-border/50 hover:bg-muted/30" {...(projectIndex === 0 ? { 'data-tour': 'time-entry-row' } : {})}>
                      <td className="p-4">
                        <div className="flex items-start gap-2">
                          {isOverAllocated && (
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm">{project.name}</div>
                            <div className="text-xs text-muted-foreground">{project.client_name || 'Internal'}</div>
                            {showAlloc && (
                              <div className="mt-1.5 space-y-0.5">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-[10px] text-muted-foreground">{decimalToHHMM(used) || '0:00'}h / {decimalToHHMM(allocated) || '0:00'}h used</span>
                                  <span className={`text-[10px] font-semibold ${isOverAllocated ? 'text-red-500' : isNearLimit ? 'text-orange-500' : 'text-green-600 dark:text-green-400'}`}>
                                    {isOverAllocated ? 'Limit reached' : `${decimalToHHMM(remaining) || '0:00'}h left`}
                                  </span>
                                </div>
                                <Progress
                                  value={Math.min(100, getUtilizationPercent(project))}
                                  className={`h-1 ${isOverAllocated ? '[&>div]:bg-red-500' : isNearLimit ? '[&>div]:bg-orange-500' : '[&>div]:bg-green-500'}`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {weekDates.map((date) => {
                        const entry = getEntryForCell(project.id, date);
                        const restricted = isDateRestricted(date);
                        const readOnly = restricted || isEntryReadOnly(entry);
                        return (
                          <td key={date} className={`p-2 text-center ${restricted || isEntryReadOnly(entry) ? 'bg-muted/30' : ''}`}>
                            <TimeCell
                              key={`${project.id}-${date}`}
                              project={project}
                              date={date}
                              entry={entry}
                              readOnly={readOnly}
                              onCellChange={handleCellChange}
                              onDescriptionSave={handleDescriptionSave}
                              catalogVerticals={catalogVerticals}
                              catalogWorkTypes={catalogWorkTypes}
                              maxHours={getMaxForCell(entry)}
                              isDesktop
                              isRejectedEntry={!!entry && !!rejectedTimesheetId && entry.timesheet_id === rejectedTimesheetId}
                            />
                          </td>
                        );
                      })}
                      <td className="p-4 text-center font-mono font-medium">
                        {decimalToHHMM(calculateProjectTotal(project.id)) || '0:00'}h
                      </td>
                      {canDelete && (
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeProjectRow(project.id)}
                            data-testid={`remove-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}

                {selectedProjects.length === 0 && showDemoRow && (
                  <tr data-tour="time-entry-row" className="border-b border-dashed border-primary/30 bg-primary/5">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-medium text-sm text-muted-foreground">Sample Project</div>
                          <div className="text-xs text-muted-foreground/50">Client Name</div>
                        </div>
                        <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold tracking-wide">DEMO</span>
                      </div>
                    </td>
                    {weekDates.map((date, i) => (
                      <td key={date} className="p-2 text-center">
                        <div className="h-8 flex items-center justify-center text-sm font-mono text-muted-foreground/40 border border-dashed border-border rounded-md mx-auto w-14">
                          {i === 0 ? '2.0' : '—'}
                        </div>
                        {i === 0 && (
                          <button
                            data-tour="note-icon"
                            className="mt-1 mx-auto flex items-center justify-center w-6 h-6 rounded-md text-amber-500 bg-amber-500/10 cursor-default"
                            tabIndex={-1}
                            onClick={e => e.preventDefault()}
                          >
                            <MessageSquarePlus className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    ))}
                    <td className="p-4 text-center font-mono font-medium text-muted-foreground/40">2.0h</td>
                    {canDelete && <td className="p-2" />}
                  </tr>
                )}

                {selectedProjects.length === 0 && !showDemoRow && (
                  <tr>
                    <td colSpan={isLocked ? 9 : 10} className="py-14 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                          <Briefcase className="w-7 h-7 text-muted-foreground/50" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {isLocked ? 'No time entries this week' : 'No projects added yet'}
                          </p>
                          {!isLocked && (
                            <p className="text-sm text-muted-foreground mt-0.5">
                              Use the dropdown above to add projects to your timesheet.
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Daily Totals Row */}
                {selectedProjects.length > 0 && (
                  <tr className="bg-muted/50 font-medium">
                    <td className="p-4 text-sm">Daily Total</td>
                    {weekDates.map((date) => (
                      <td key={date} className="p-4 text-center font-mono text-sm">
                        {calculateDayTotal(date) > 0 ? `${decimalToHHMM(calculateDayTotal(date))}h` : '—'}
                      </td>
                    ))}
                    <td className="p-4 text-center font-mono text-lg text-accent">
                      {decimalToHHMM(calculateWeekTotal()) || '0:00'}h
                    </td>
                    {canDelete && <td></td>}
                  </tr>
                )}
              </tbody>
            </table>
          )}
          </div>
        </CardContent>
      </Card>}

      {/* Time Grid — Mobile */}
      {activeTab === 'you' && <div className="md:hidden space-y-3" data-testid="time-grid-mobile">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : selectedProjects.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <div>
              <p className="font-medium text-foreground">
                {isLocked ? 'No time entries this week' : 'No projects added yet'}
              </p>
              {!isLocked && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  Use the dropdown above to add projects to your timesheet.
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {selectedProjects.map((project) => {
              const allocated = getAllocatedHours(project);
              const remaining = getRemainingHours(project);
              const used = getHoursUsed(project);
              const isOverAllocated = allocated > 0 && used > allocated;
              const isNearLimit = !isOverAllocated && allocated > 0 && getUtilizationPercent(project) >= 80;
              const showAlloc = allocated > 0 && !isAdmin && !isSuperAdmin;
              const projectTotal = calculateProjectTotal(project.id);

              const getMaxForCell = (cellEntry) => {
                if (!allocated || isAdmin || isSuperAdmin) return null;
                return Math.max(0, remaining + (cellEntry?.hours || 0));
              };

              return (
                <div key={project.id} className="border border-border rounded-lg bg-card">
                  {/* Project header */}
                  <div className="px-4 pt-3 pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isOverAllocated && <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm leading-tight truncate">{project.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{project.client_name || 'Internal'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <div className="text-right">
                        <div className="text-[10px] text-muted-foreground">Total</div>
                        <div className="text-sm font-bold font-mono">{decimalToHHMM(projectTotal) || '0:00'}h</div>
                      </div>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeProjectRow(project.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {showAlloc && (
                    <div className="mt-1.5 space-y-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-muted-foreground">{decimalToHHMM(used) || '0:00'}h / {decimalToHHMM(allocated) || '0:00'}h used</span>
                        <span className={`text-[10px] font-semibold ${isOverAllocated ? 'text-red-500' : isNearLimit ? 'text-orange-500' : 'text-green-600 dark:text-green-400'}`}>
                          {isOverAllocated ? 'Limit reached' : `${decimalToHHMM(remaining) || '0:00'}h left`}
                        </span>
                      </div>
                      <Progress
                        value={Math.min(100, getUtilizationPercent(project))}
                        className={`h-1 ${isOverAllocated ? '[&>div]:bg-red-500' : isNearLimit ? '[&>div]:bg-orange-500' : '[&>div]:bg-green-500'}`}
                      />
                    </div>
                  )}
                  </div>

                  {/* Day cells */}
                  <div className="grid grid-cols-5 px-1 pb-3 pt-1" style={{ gap: 4 }}>
                    {weekDates.slice(0, 5).map((date, i) => {
                      const entry = getEntryForCell(project.id, date);
                      const restricted = isDateRestricted(date);
                      const readOnly = restricted || isEntryReadOnly(entry);
                      return (
                        <div key={date} className="flex flex-col items-center" style={{ minWidth: 0 }}>
                          <span className={`text-[10px] font-medium leading-none mb-0.5 ${restricted ? 'text-muted-foreground/40' : 'text-muted-foreground'}`}>
                            {dayNames[i]}
                          </span>
                          <span className="text-[10px] text-muted-foreground/50 leading-none mb-1">
                            {new Date(date).getDate()}
                          </span>
                          <TimeCell
                            key={`${project.id}-${date}`}
                            project={project}
                            date={date}
                            entry={entry}
                            readOnly={readOnly}
                            onCellChange={handleCellChange}
                            onDescriptionSave={handleDescriptionSave}
                            catalogVerticals={catalogVerticals}
                            catalogWorkTypes={catalogWorkTypes}
                            maxHours={getMaxForCell(entry)}
                            isRejectedEntry={!!entry && !!rejectedTimesheetId && entry.timesheet_id === rejectedTimesheetId}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Mobile Daily Total */}
            <div className="border border-border rounded-lg bg-muted/50 px-3 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Daily Total</span>
                <span className="text-sm font-bold font-mono text-accent">{decimalToHHMM(calculateWeekTotal()) || '0:00'}h</span>
              </div>
              <div className="grid grid-cols-5" style={{ gap: 4 }}>
                {weekDates.slice(0, 5).map((date, i) => (
                  <div key={date} className="flex flex-col items-center" style={{ minWidth: 0 }}>
                    <span className="text-[10px] text-muted-foreground leading-none mb-0.5">{dayNames[i]}</span>
                    <span className="text-xs font-mono font-semibold">
                      {calculateDayTotal(date) > 0 ? `${decimalToHHMM(calculateDayTotal(date))}h` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>}

      {/* Actions */}
      {activeTab === 'you' && (() => {
        // New unlinked entries = entries not tied to the approved timesheet
        const newEntries = entries.filter(e => !e.timesheet_id || !approvedTimesheetIds.has(e.timesheet_id));
        const newHours = newEntries.reduce((s, e) => s + e.hours, 0);
        const showSubmit = selectedProjects.length > 0 && newHours > 0;

        // Disable submit for non-admins when any project has hit/exceeded its allocation
        const overAllocatedProjects = (!isAdmin && !isSuperAdmin)
          ? selectedProjects.filter(p => {
              const allocated = getAllocatedHours(p);
              return allocated > 0 && getHoursUsed(p) > allocated;
            })
          : [];
        const isAnyOverAllocated = overAllocatedProjects.length > 0;
        const submitDisabled = isAnyOverAllocated;
        const submitTitle = isAnyOverAllocated
          ? `Allocation limit reached on: ${overAllocatedProjects.map(p => p.name).join(', ')}. Contact your manager to increase your allocation.`
          : undefined;

        return showSubmit ? (
          <div className="sticky bottom-0 z-20 bg-background/95 backdrop-blur-sm border-t border-border py-3 px-1">
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="gap-2" onClick={fetchData}>
                <Save className="w-4 h-4" /> Refresh
              </Button>
              <div className="flex flex-col items-end gap-1">
                <Button
                  className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setShowSubmitConfirm(true)}
                  disabled={submitDisabled}
                  title={submitTitle}
                  data-testid="submit-timesheet-btn"
                >
                  <Send className="w-4 h-4" /> Submit Timesheet
                </Button>
                {isAnyOverAllocated && (
                  <p className="text-xs text-red-500 text-right max-w-xs">
                    Allocation limit reached on <span className="font-medium">{overAllocatedProjects.map(p => p.name).join(', ')}</span>. Contact your manager to increase your allocation.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Project Allocation Summary */}
      {activeTab === 'you' && selectedProjects.length > 0 && (
        <Card data-testid="allocation-summary">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Project Hours Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <TooltipProvider delayDuration={300}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedProjects.map((project) => {
                  const allocated = getAllocatedHours(project);
                  const used = getHoursUsed(project);
                  const remaining = getRemainingHours(project);
                  const percent = getUtilizationPercent(project);
                  const isOverAllocated = allocated > 0 && used > allocated;
                  const isNearLimit = percent >= 80 && percent < 100;

                  const myServices = (project.services || []).filter(svc =>
                    (svc.user_allocations || []).some(ua => ua.user_id === user?.id)
                  );

                  const card = (
                    <div
                      className={`p-4 border rounded-sm cursor-default ${
                        isOverAllocated ? 'border-red-500/50 bg-red-500/5' :
                        isNearLimit ? 'border-orange-500/50 bg-orange-500/5' :
                        'border-border'
                      }`}
                      data-testid={`project-summary-${project.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-medium">{project.name}</div>
                          <div className="text-xs text-muted-foreground">{project.client_name || 'Internal'}</div>
                        </div>
                        {isOverAllocated && (
                          <span className="text-xs bg-red-500/20 text-red-600 dark:text-red-400 px-2 py-1 rounded-sm">
                            Over Budget
                          </span>
                        )}
                        {isNearLimit && !isOverAllocated && (
                          <span className="text-xs bg-orange-500/20 text-orange-600 dark:text-orange-400 px-2 py-1 rounded-sm">
                            Near Limit
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Used</span>
                          <span className="font-mono">{decimalToHHMM(used) || '0:00'}h</span>
                        </div>
                        <Progress
                          value={Math.min(percent, 100)}
                          className={`h-2 ${isOverAllocated ? '[&>div]:bg-red-500' : isNearLimit ? '[&>div]:bg-orange-500' : ''}`}
                        />
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Allocated</span>
                          <span className="font-mono">{decimalToHHMM(allocated) || '0:00'}h</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-border">
                          <span className={`font-medium ${isOverAllocated ? 'text-red-600 dark:text-red-400' : 'text-accent'}`}>
                            {isOverAllocated ? 'Over by' : 'Remaining'}
                          </span>
                          <span className={`font-mono font-bold ${isOverAllocated ? 'text-red-600 dark:text-red-400' : 'text-accent'}`}>
                            {isOverAllocated ? `${decimalToHHMM(Math.abs(remaining))}h` : `${decimalToHHMM(remaining) || '0:00'}h`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );

                  if (!myServices.length) return <div key={project.id}>{card}</div>;

                  return (
                    <Tooltip key={project.id}>
                      <TooltipTrigger asChild>{card}</TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-xs p-3 bg-popover text-popover-foreground border border-border shadow-lg rounded-lg"
                      >
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">My Service Tasks</div>
                        <div className="space-y-1.5">
                          {myServices.map(svc => {
                            const vertical = catalogVerticals.find(v => v.id === svc.vertical_id);
                            const myAlloc = (svc.user_allocations || []).find(ua => ua.user_id === user?.id);
                            const wts = (svc.work_type_ids || []).map(id => catalogWorkTypes.find(wt => wt.id === id)?.name).filter(Boolean);
                            return (
                              <div key={svc.vertical_id} className="flex flex-wrap items-center gap-1">
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30 font-semibold">
                                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: vertical?.color || '#6366f1' }} />
                                  {vertical?.name || 'Service'}
                                  {(myAlloc?.hours || 0) > 0 && <span className="font-mono opacity-75 ml-0.5">· {myAlloc.hours}h</span>}
                                </span>
                                {wts.map(wt => (
                                  <span key={wt} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium">
                                    {wt}
                                  </span>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          </CardContent>
        </Card>
      )}

      {/* Submit Confirmation Dialog */}
      {/* (kept outside tab guard so the dialog doesn't unmount mid-open) */}
      <AlertDialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Timesheet?</AlertDialogTitle>
            <AlertDialogDescription>
              Submit your logged hours for this week for admin review. You can still add or edit entries after submission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitTimesheet}>Submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Team Tab — per-member grids */}
      {activeTab === 'team' && (
        teamLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {teamMembers.map(member => {
              const data = teamData[member.id] || { entries: [], timesheet: null };
              const memberProjectIds = [...new Set(data.entries.map(e => e.project_id))];
              const memberProjects = memberProjectIds.map(id => teamProjectsMap[id]).filter(Boolean);
              const weekTotal = data.entries.reduce((s, e) => s + e.hours, 0);
              const status = data.timesheet?.status;
              const statusStyles = {
                approved: 'bg-green-500/15 text-green-600 dark:text-green-400',
                submitted: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
                rejected: 'bg-red-500/15 text-red-600 dark:text-red-400',
                draft: 'bg-muted text-muted-foreground',
              };
              return (
                <Card key={member.id}>
                  <CardHeader className="pb-2 pt-4 px-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold">
                        {member.first_name} {member.last_name}
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground font-mono">{decimalToHHMM(weekTotal) || '0:00'}h this week</span>
                        {status ? (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusStyles[status] || statusStyles.draft}`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">No timesheet</span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full min-w-[900px]">
                      <thead>
                        <tr className="border-b border-t border-border">
                          <th className="text-left p-4 font-medium text-muted-foreground w-[250px]">Project</th>
                          {weekDates.map((date, i) => (
                            <th key={date} className="text-center p-4 font-medium text-muted-foreground w-[80px]">
                              <div className="text-xs">{dayNames[i]}</div>
                              <div className="text-sm mt-1">{new Date(date).getDate()}</div>
                            </th>
                          ))}
                          <th className="text-center p-4 font-medium text-muted-foreground w-[80px]">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberProjects.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="p-6 text-center text-sm text-muted-foreground">
                              No time logged this week
                            </td>
                          </tr>
                        ) : (
                          <>
                            {memberProjects.map(project => {
                              const projectTotal = data.entries
                                .filter(e => e.project_id === project.id)
                                .reduce((s, e) => s + e.hours, 0);
                              return (
                                <tr key={project.id} className="border-b border-border/50 hover:bg-muted/30">
                                  <td className="p-4">
                                    <div className="font-medium text-sm">{project.name}</div>
                                    <div className="text-xs text-muted-foreground">{project.client_name || 'Internal'}</div>
                                  </td>
                                  {weekDates.map(date => {
                                    const entry = data.entries.find(e => e.project_id === project.id && e.date === date);
                                    return (
                                      <td key={date} className="p-2 text-center">
                                        <TimeCell
                                          project={project}
                                          date={date}
                                          entry={entry}
                                          readOnly={true}
                                          onCellChange={() => {}}
                                          onDescriptionSave={() => {}}
                                          catalogVerticals={catalogVerticals}
                                          catalogWorkTypes={catalogWorkTypes}
                                          isDesktop
                                        />
                                      </td>
                                    );
                                  })}
                                  <td className="p-4 text-center font-mono font-medium">
                                    {decimalToHHMM(projectTotal) || '0:00'}h
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="bg-muted/50 font-medium">
                              <td className="p-4 text-sm">Daily Total</td>
                              {weekDates.map(date => (
                                <td key={date} className="p-4 text-center font-mono text-sm">
                                  {(() => { const d = data.entries.filter(e => e.date === date).reduce((s, e) => s + e.hours, 0); return d > 0 ? `${decimalToHHMM(d)}h` : '—'; })()}
                                </td>
                              ))}
                              <td className="p-4 text-center font-mono text-lg text-accent">
                                {decimalToHHMM(weekTotal) || '0:00'}h
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

const TimeCell = ({ project, date, entry, readOnly, onCellChange, onDescriptionSave, catalogVerticals, catalogWorkTypes, isDesktop, maxHours, isRejectedEntry }) => {
  const [inputValue, setInputValue] = useState(decimalToHHMM(entry?.hours) || '');
  const [autoOpen, setAutoOpen] = useState(false);
  const pendingOpenRef = React.useRef(false);

  useEffect(() => {
    setInputValue(decimalToHHMM(entry?.hours) || '');
  }, [entry?.hours]);

  // When entry is created after clicking the grey icon, trigger popup open
  useEffect(() => {
    if (pendingOpenRef.current && entry) {
      setAutoOpen(true);
      pendingOpenRef.current = false;
    }
  }, [entry]);

  const numVal = hhmmToDecimal(inputValue);
  const isOverLimit = maxHours != null && maxHours >= 0 && numVal > maxHours;
  const hasValue = numVal > 0;
  const showDescIcon = hasValue && !readOnly;

  const handleChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*:?\d*$/.test(val)) setInputValue(val);
  };
  const handleBlur = () => onCellChange(project.id, date, hhmmToDecimal(inputValue), entry);
  const handlePlaceholderClick = () => { pendingOpenRef.current = true; };

  const sharedInputProps = {
    type: 'text',
    inputMode: 'text',
    value: inputValue,
    onChange: handleChange,
    onBlur: handleBlur,
    disabled: readOnly,
    placeholder: '0:00',
  };

  const isFlagged = isRejectedEntry || (entry && entry.rejection_note !== undefined);

  return (
    <>
      {isDesktop ? (
        <Input
          {...sharedInputProps}
          className={`time-cell text-center font-mono text-sm ${entry?.hours > 0 ? 'has-value' : ''} ${readOnly ? 'opacity-40 cursor-not-allowed' : ''} ${isFlagged ? 'border-red-400 bg-red-500/5' : ''} ${isOverLimit ? '!border-orange-500 !bg-orange-500/5' : ''}`}
          data-testid={`time-cell-${project.id}-${date}`}
        />
      ) : (
        <input
          {...sharedInputProps}
          style={{ minWidth: 0 }}
          className={`w-full h-8 text-center font-mono text-xs border rounded bg-background focus:outline-none focus:ring-1 ${entry?.hours > 0 ? 'bg-accent/5 border-accent/30 font-semibold' : 'border-border'} ${readOnly ? 'opacity-40 cursor-not-allowed bg-muted/30' : ''} ${isFlagged ? '!border-red-400 !bg-red-500/5' : ''} ${isOverLimit ? '!border-orange-500 !bg-orange-500/5' : ''}`}
        />
      )}
      {isOverLimit && !readOnly && (
        <div className="text-[10px] text-orange-500 text-center leading-tight mt-0.5 whitespace-nowrap">
          max {decimalToHHMM(maxHours)}
        </div>
      )}
      {isFlagged && (
        <div
          className="text-[10px] text-red-500 text-center leading-tight mt-0.5 cursor-help"
          title={entry?.rejection_note || 'Timesheet rejected — edit and resubmit'}
        >
          ⚠ {entry?.rejection_note ? 'flagged' : 'rejected'}
        </div>
      )}
      {showDescIcon && (
        entry ? (
          <DescriptionCell
            entry={entry}
            project={project}
            date={date}
            onSave={onDescriptionSave}
            catalogVerticals={catalogVerticals}
            catalogWorkTypes={catalogWorkTypes}
            autoOpen={autoOpen}
          />
        ) : (
          <button
            type="button"
            onMouseDown={handlePlaceholderClick}
            className="mt-1 mx-auto flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground/30 hover:text-amber-400 transition-colors"
            title="Save hours first, then add description"
          >
            <MessageSquarePlus className="w-3.5 h-3.5" />
          </button>
        )
      )}
    </>
  );
};

const DescriptionCell = ({ entry, project, date, onSave, catalogVerticals = [], catalogWorkTypes = [], autoOpen = false }) => {
  const [open, setOpen] = useState(false);
  const discardRef = React.useRef(false);

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);
  const [draft, setDraft] = useState('');
  const [draftBlockers, setDraftBlockers] = useState('');
  const [selectedVertical, setSelectedVertical] = useState('');
  const [selectedWorkType, setSelectedWorkType] = useState('');

  const projectServices = project.services || [];
  const hasServices = projectServices.length > 0 && catalogVerticals.length > 0;

  const availableVerticals = hasServices
    ? projectServices.map(svc => catalogVerticals.find(v => v.id === svc.vertical_id)).filter(Boolean)
    : [];

  const availableWorkTypes = selectedVertical
    ? (() => {
        const svc = projectServices.find(s => s.vertical_id === selectedVertical);
        if (!svc) return [];
        if (svc.work_type_ids.length === 0) return catalogWorkTypes.filter(w => w.vertical_id === selectedVertical);
        return svc.work_type_ids.map(id => catalogWorkTypes.find(w => w.id === id)).filter(Boolean);
      })()
    : [];

  const hasDescription = !!entry?.description?.trim();
  const isAnnotated = hasDescription;

  const handleOpenChange = (isOpen) => {
    if (isOpen) {
      discardRef.current = false;
      setDraft(entry?.description || '');
      setDraftBlockers(entry?.blockers || '');
      setSelectedVertical(entry?.vertical_id || '');
      setSelectedWorkType(entry?.work_type_id || '');
    } else if (!discardRef.current) {
      // Closed by clicking outside or pressing Escape — auto-save
      onSave(entry, draft, selectedVertical, selectedWorkType, draftBlockers);
    }
    discardRef.current = false;
    setOpen(isOpen);
  };

  const handleSave = () => {
    discardRef.current = true; // mark as intentional save so handleOpenChange doesn't double-save
    onSave(entry, draft, selectedVertical, selectedWorkType, draftBlockers);
    setOpen(false);
  };

  const handleCancel = () => {
    discardRef.current = true; // discard — don't auto-save on close
    setOpen(false);
  };

  const verticalName = entry?.vertical_id ? catalogVerticals.find(v => v.id === entry.vertical_id)?.name : null;
  const workTypeName = entry?.work_type_id ? catalogWorkTypes.find(w => w.id === entry.work_type_id)?.name : null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-tour="note-icon"
          className={`mt-1 mx-auto flex items-center justify-center w-6 h-6 rounded-md transition-all ${
            isAnnotated
              ? 'text-accent bg-accent/10 hover:bg-accent/20'
              : 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
          }`}
          title={verticalName ? `${verticalName}${workTypeName ? ' › ' + workTypeName : ''}${hasDescription ? ' · ' + entry.description : ''}` : (hasDescription ? entry.description : 'Add description (required)')}
        >
          {isAnnotated
            ? <MessageSquare className="w-3.5 h-3.5" />
            : <MessageSquarePlus className="w-3.5 h-3.5" />
          }
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="center" side="bottom">
        <div className="px-4 py-3 border-b border-border bg-muted/50 rounded-t-md">
          <p className="text-xs font-semibold text-foreground">{project.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </p>
        </div>
        <div className="p-3 space-y-2">
          {hasServices && (
            <>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Service Vertical</label>
                <select
                  value={selectedVertical}
                  onChange={(e) => { setSelectedVertical(e.target.value); setSelectedWorkType(''); }}
                  className="w-full text-sm border border-border rounded-md px-2.5 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                >
                  <option value="">— Select vertical —</option>
                  {availableVerticals.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              {selectedVertical && availableWorkTypes.length > 0 && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Work Type</label>
                  <select
                    value={selectedWorkType}
                    onChange={(e) => setSelectedWorkType(e.target.value)}
                    className="w-full text-sm border border-border rounded-md px-2.5 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
                  >
                    <option value="">— Select work type —</option>
                    {availableWorkTypes.map(wt => (
                      <option key={wt.id} value={wt.id}>{wt.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">What did you work on?</label>
            <textarea
              className="w-full text-sm border border-border rounded-md p-2.5 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent placeholder:text-muted-foreground/50"
              rows={2}
              placeholder="Describe what you worked on..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus={!hasServices}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Any blockers or challenges?</label>
            <textarea
              className="w-full text-sm border border-border rounded-md p-2.5 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-destructive/50 focus:border-destructive placeholder:text-muted-foreground/50"
              rows={2}
              placeholder="Any blockers or challenges? (optional)"
              value={draftBlockers}
              onChange={(e) => setDraftBlockers(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{entry?.blockers ? '⚠ Has blockers' : ''}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="sm" className="h-7 px-3 text-xs bg-accent text-accent-foreground hover:bg-accent/90" onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TimeEntryPage;
